import { endOfDay, startOfDay } from "date-fns";
import React from "react";
import { z } from "zod";

import {
	FormCheckboxGroup,
	FormDateRangePicker,
	FormRadioButtons,
	FormSingleSelect,
	PanelFilters,
} from "@performyard/py-components";

import { capitalizeWord } from "../../../../utils/stringUtils";

import { config } from "../config";
import type { MeetingsTableFilters } from "../schema";

type FilterPanelProps = {
	isOpen: boolean;
	onClose: () => void;
	onApplyFilters: (filters: MeetingsTableFilters) => void;
	filters: MeetingsTableFilters;
	recurringSeriesOptions?: { value: string; label: string }[];
};

const DateRangeFilterSchema = z
	.object({
		from: z.date().optional(),
		to: z.date().optional(),
	})
	.optional();

const FilterPanelSchema = z.object({
	status: z.array(z.enum(config.statusOptions)).optional(),
	meetingDateRange: DateRangeFilterSchema,
	recurringSeriesId: z.string().optional(),
	privateNotes: z.enum(config.privateNotesOptions).optional(),
});

const toDateRange = (from?: Date, to?: Date) => (from || to ? { from, to } : undefined);

const FilterPanel = ({
	isOpen,
	onClose,
	onApplyFilters,
	filters,
	recurringSeriesOptions = [],
}: FilterPanelProps): React.ReactElement => {
	const statusOptions = config.statusOptions.map((status) => ({
		value: status,
		label: capitalizeWord(status),
	}));

	const privateNotesRadioOptions = config.privateNotesOptions.map((option) => ({
		value: option,
		label: option === "any" ? "Any" : option === "has-private-notes" ? "Has Private Notes" : "No Private Notes",
	}));

	return (
		<PanelFilters
			isOpen={isOpen}
			onClose={onClose}
			schema={FilterPanelSchema}
			defaultValues={{
				status: filters.status ?? [],
				meetingDateRange: toDateRange(filters.meetingDateStart, filters.meetingDateEnd),
				recurringSeriesId: filters.recurringSeriesId,
				privateNotes: filters.privateNotes ?? "any",
			}}
			onSubmit={({ value }) => {
				onApplyFilters({
					status: value.status ?? [],
					meetingDateStart: value.meetingDateRange?.from
						? startOfDay(value.meetingDateRange.from)
						: undefined,
					meetingDateEnd: value.meetingDateRange?.to ? endOfDay(value.meetingDateRange.to) : undefined,
					recurringSeriesId: value.recurringSeriesId,
					privateNotes: value.privateNotes ?? "any",
				});
				onClose();
			}}
		>
			<FormCheckboxGroup id="status" label="Status" options={statusOptions} />
			<div className="h-4" />
			<FormDateRangePicker
				id="meetingDateRange"
				label="Meeting Date"
				popoverContentClassName="z-[1100]"
				menuAlign="start"
				hasClearButton
			/>
			{recurringSeriesOptions.length > 0 && (
				<>
					<div className="h-4" />
					<FormSingleSelect<string>
						id="recurringSeriesId"
						label="Recurring Meeting Series"
						options={recurringSeriesOptions}
						selectionVariant="select"
						placeholder="Make a selection"
						isClearable
					/>
				</>
			)}
			<div className="h-4" />
			<FormRadioButtons id="privateNotes" label="Private Notes" options={privateNotesRadioOptions} />
		</PanelFilters>
	);
};

export default FilterPanel;
